import React, { useState, useEffect } from "react";
import axios from "axios";
import config from "../config";
import NotificationCart from "./NotificationCart";

const CustomerLogin = ({ onLoginSuccess, onClose }) => {
  const [passwordConfirmation, setPasswordConfirmation] = useState("");
  const [resetEmail, setResetEmail] = useState("");
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const [formMode, setFormMode] = useState("login");
  const [userData, setUserData] = useState({
    email: "",
    password: "",
    customer_name: "",
    phone_number: "",
    restaurant_id: "",
  });
  const [notificationMessage, setNotificationMessage] = useState("");
  const [notificationStatus, setNotificationStatus] = useState("");
  const [isNotification, setIsNotification] = useState(false);

  useEffect(() => {
    const storedUserData = localStorage.getItem("customerData");
    if (storedUserData && storedUserData !== "undefined") {
      try {
        const parsedData = JSON.parse(storedUserData);
        setUserData({
          email: String(parsedData.email || ""),
          password: String(parsedData.password || ""),
          customer_name: String(parsedData.customer_name || ""),
          phone_number: String(parsedData.phone_number || ""),
          restaurant_id: String(parsedData.restaurant_id || ""),
        });
        onLoginSuccess(parsedData);
      } catch (error) {
        console.error("Error parsing stored user data:", error);
      }
    }
  }, [onLoginSuccess]);

  // Safe input change handler
  const handleInputChange = (field, value) => {
    setUserData((prev) => ({
      ...prev,
      [field]: String(value || ""),
    }));
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(
        `${config.API_BASE_URL}/customer/login`,
        userData
      );

      const safeUserData = {
        email: String(response.data.email || ""),
        password: String(response.data.password || ""),
        customer_name: String(response.data.customer_name || ""),
        phone_number: String(response.data.phone_number || ""),
        restaurant_id: String(response.data.restaurant_id || ""),
        token: response.data.token,
      };

      setUserData(safeUserData);
      localStorage.setItem("customerData", JSON.stringify(safeUserData));
      localStorage.setItem("CustomerToken", response.data.token);
      setNotificationMessage("Login successful");
      setNotificationStatus("success");
      setIsNotification(true);
      setTimeout(() => {
        setIsNotification(false);
        onLoginSuccess(response.data);
      }, 2000);
    } catch (error) {
      setNotificationMessage("Invalid email or password");
      setNotificationStatus("error");
      setIsNotification(true);
      setTimeout(() => {
        setIsNotification(false);
      }, 9000);
    }
  };

  const handleClose = () => {
    if (typeof onClose === "function") {
      onClose();
    }
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    if (!termsAccepted) {
      setNotificationMessage("Please accept the Terms and Conditions");
      setNotificationStatus("error");
      setIsNotification(true);
      setTimeout(() => {
        setIsNotification(false);
      }, 9000);
      return;
    }
    if (userData.password !== passwordConfirmation) {
      setNotificationMessage("Passwords do not match");
      setNotificationStatus("error");
      setIsNotification(true);
      setTimeout(() => {
        setIsNotification(false);
      }, 9000);
      return;
    }

    // Get restaurant_id from current_restaurant_details
    let restaurant_id = null;
    const restaurantDetails = localStorage.getItem(
      "current_restaurant_details"
    );

    if (restaurantDetails) {
      try {
        const parsedDetails = JSON.parse(restaurantDetails);
        restaurant_id = parsedDetails.restaurant_id;
      } catch (error) {
        console.error("Error parsing restaurant details:", error);
      }
    }

    if (!restaurant_id) {
      const storedRestaurantId = localStorage.getItem("current_restaurant_id");
      if (storedRestaurantId) {
        restaurant_id = parseInt(storedRestaurantId);
      }
    }

    if (!restaurant_id || isNaN(restaurant_id)) {
      setNotificationMessage(
        "Restaurant information is missing. Please refresh the page and try again."
      );
      setNotificationStatus("error");
      setIsNotification(true);
      setTimeout(() => {
        setIsNotification(false);
      }, 9000);
      return;
    }

    try {
      const response = await axios.post(
        `${config.API_BASE_URL}/customer/create`,
        {
          ...userData,
          restaurant_id: restaurant_id,
        }
      );

      const safeResponseData = {
        email: String(response.data.data?.email || ""),
        password: String(response.data.data?.password || ""),
        customer_name: String(response.data.data?.customer_name || ""),
        phone_number: String(response.data.data?.phone_number || ""),
        restaurant_id: String(response.data.data?.restaurant_id || ""),
      };

      setUserData(safeResponseData);
      localStorage.setItem("customerData", JSON.stringify(safeResponseData));
      localStorage.setItem("CustomerToken", response.data.data.token);
      setNotificationMessage("Account created successfully");
      setNotificationStatus("success");
      setIsNotification(true);
      setTimeout(() => {
        setIsNotification(false);
        onLoginSuccess(response.data);
      }, 9000);
    } catch (error) {
      const errorMessage =
        error.response?.data?.error ||
        "Error creating account. Please try again.";
      setNotificationMessage(errorMessage);
      setNotificationStatus("error");
      setIsNotification(true);
      setTimeout(() => {
        setIsNotification(false);
      }, 9000);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(
        `${config.API_BASE_URL}/customer/forgot-password`,
        {
          email: resetEmail,
        }
      );
      setNotificationMessage(
        response.data.message || "Password reset link sent to your email"
      );
      setNotificationStatus("success");
      setIsNotification(true);
      setTimeout(() => {
        setIsNotification(false);
        setFormMode("login");
      }, 9000);
    } catch (error) {
      setNotificationMessage("Error resetting password. Please try again.");
      setNotificationStatus("error");
      setIsNotification(true);
      setTimeout(() => {
        setIsNotification(false);
      }, 9000);
    }
  };

  return (
    <div className="w-full">
      {/* Terms and Conditions Modal */}
      {showTerms && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] overflow-hidden flex flex-col">
            <div className="p-6 border-b border-gray-200 flex justify-between items-center">
              <h3 className="text-xl font-semibold text-gray-800">
                Terms and Conditions
              </h3>
              <button
                onClick={() => setShowTerms(false)}
                className="text-gray-500 hover:text-gray-700 text-2xl"
              >
                ×
              </button>
            </div>
            <div className="overflow-y-auto p-6">
              <h4 className="text-lg font-medium text-gray-700 mb-4">
                1. Acceptance of Terms
              </h4>
              <p className="text-gray-600 mb-4">
                By creating an account with our service, you agree to be bound
                by these Terms and Conditions. If you do not agree to all terms,
                do not use our service.
              </p>
              <h4 className="text-lg font-medium text-gray-700 mb-4">
                2. Account Registration
              </h4>
              <p className="text-gray-600 mb-4">
                You must provide accurate and complete information when creating
                an account. You are responsible for maintaining the
                confidentiality of your password and account information.
              </p>
              <h4 className="text-lg font-medium text-gray-700 mb-4">
                3. User Responsibilities
              </h4>
              <p className="text-gray-600 mb-4">
                You agree to use the service only for lawful purposes and in a
                way that does not infringe the rights of, restrict or inhibit
                anyone else's use and enjoyment of the service.
              </p>
              <h4 className="text-lg font-medium text-gray-700 mb-4">
                4. Privacy Policy
              </h4>
              <p className="text-gray-600 mb-4">
                Your privacy is important to us. Our Privacy Policy explains how
                we collect, use, and protect your personal information. By using
                our service, you agree to the collection and use of information
                in accordance with our Privacy Policy.
              </p>
              <h4 className="text-lg font-medium text-gray-700 mb-4">
                5. Modifications to Terms
              </h4>
              <p className="text-gray-600 mb-4">
                We reserve the right to modify these terms at any time. We will
                provide notice of significant changes through our service.
                Continued use of the service after changes constitutes
                acceptance of the modified terms.
              </p>
              <h4 className="text-lg font-medium text-gray-700 mb-4">
                6. Limitation of Liability
              </h4>
              <p className="text-gray-600 mb-4">
                Our service is provided "as is" without any warranties. We are
                not liable for any indirect, incidental, special, consequential
                or punitive damages resulting from your use of or inability to
                use the service.
              </p>
            </div>
            <div className="p-4 border-t border-gray-200 bg-gray-50 flex justify-end">
              <button
                onClick={() => {
                  setShowTerms(false);
                  setTermsAccepted(true);
                }}
                className="px-4 py-2 bg-[#4e7ab4] text-white rounded-md hover:bg-[#3a5d8a] transition-colors"
              >
                I Accept
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="max-w-md w-[100%] sm:w-full mx-auto bg-white rounded-lg shadow-lg relative p-4 sm:p-8">
        <button
          className="absolute top-4 right-4 text-2xl text-gray-600 hover:text-[#4e7ab4] transition-colors duration-300"
          onClick={handleClose}
        >
          ×
        </button>
        <h1 className="text-3xl font-bold text-center text-gray-800 mb-8">
          Customer Login
        </h1>

        {isNotification && (
          <NotificationCart
            message={notificationMessage}
            status={notificationStatus}
          />
        )}

        {formMode === "forgotPassword" && (
          <div className="space-y-6">
            <h2 className="text-2xl font-semibold text-gray-700">
              Forgot Password
            </h2>
            <form onSubmit={handleForgotPassword} className="space-y-4">
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  Email:
                </label>
                <input
                  type="email"
                  value={resetEmail}
                  onChange={(e) => setResetEmail(e.target.value || "")}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#4e7ab4] focus:border-transparent"
                />
              </div>
              <div className="flex flex-col space-y-3">
                <button
                  type="submit"
                  className="w-full py-2 px-4 bg-[#abbfff] hover:bg-[#4e7ab4] text-white font-semibold rounded-md transition-colors duration-300"
                >
                  Send Reset Link
                </button>
                <button
                  type="button"
                  onClick={() => setFormMode("login")}
                  className="text-[#4e7ab4] hover:text-[#23354d] text-sm font-medium transition-colors"
                >
                  Back to Login
                </button>
              </div>
            </form>
          </div>
        )}

        {formMode === "login" && (
          <div className="space-y-6">
            <h2 className="text-2xl font-semibold text-gray-700">Log In</h2>
            <form onSubmit={handleLogin} className="space-y-4">
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  Email:
                </label>
                <input
                  type="email"
                  value={userData.email}
                  onChange={(e) => handleInputChange("email", e.target.value)}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#4e7ab4] focus:border-transparent"
                />
              </div>
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  Password:
                </label>
                <input
                  type="password"
                  value={userData.password}
                  onChange={(e) =>
                    handleInputChange("password", e.target.value)
                  }
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#4e7ab4] focus:border-transparent"
                />
              </div>
              <div className="flex flex-col space-y-3">
                <button
                  type="submit"
                  className="w-full py-2 px-4 bg-[#abbfff] hover:bg-[#4e7ab4] text-white font-semibold rounded-md transition-colors duration-300"
                >
                  Log In
                </button>
                <button
                  type="button"
                  onClick={() => setFormMode("signup")}
                  className="w-full py-2 px-4 bg-[#4e7ab4] hover:bg-[#23354d] text-white font-semibold rounded-md transition-colors duration-300"
                >
                  Create Account
                </button>
                <button
                  onClick={() => setFormMode("forgotPassword")}
                  className="text-[#4e7ab4] hover:text-[#23354d] text-sm font-medium transition-colors"
                >
                  Forgot Password?
                </button>
              </div>
            </form>
          </div>
        )}

        {formMode === "signup" && (
          <div className="space-y-6">
            <h2 className="text-2xl font-semibold text-gray-700">
              Create Account
            </h2>
            <form onSubmit={handleSignup} className="space-y-4">
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  Name:
                </label>
                <input
                  type="text"
                  value={userData.customer_name}
                  onChange={(e) =>
                    handleInputChange("customer_name", e.target.value)
                  }
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#4e7ab4] focus:border-transparent"
                />
              </div>
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  Email:
                </label>
                <input
                  type="email"
                  value={userData.email}
                  onChange={(e) => handleInputChange("email", e.target.value)}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#4e7ab4] focus:border-transparent"
                />
              </div>
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  Phone Number:
                </label>
                <input
                  type="tel"
                  value={userData.phone_number}
                  onChange={(e) =>
                    handleInputChange("phone_number", e.target.value)
                  }
                  pattern="[0-9]{10}"
                  maxLength="10"
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#4e7ab4] focus:border-transparent"
                />
              </div>
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  Password:
                </label>
                <input
                  type="password"
                  value={userData.password}
                  onChange={(e) =>
                    handleInputChange("password", e.target.value)
                  }
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#4e7ab4] focus:border-transparent"
                />
              </div>
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  Confirm Password:
                </label>
                <input
                  type="password"
                  value={passwordConfirmation}
                  onChange={(e) =>
                    setPasswordConfirmation(e.target.value || "")
                  }
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#4e7ab4] focus:border-transparent"
                />
              </div>
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={termsAccepted}
                  onChange={(e) => setTermsAccepted(e.target.checked)}
                  required
                  className="w-4 h-4 text-[#4e7ab4] border-gray-300 rounded focus:ring-[#4e7ab4]"
                />
                <label className="text-sm text-gray-600">
                  I agree to the{" "}
                  <button
                    type="button"
                    onClick={() => setShowTerms(true)}
                    className="text-[#4e7ab4] hover:text-[#23354d] hover:underline focus:outline-none"
                  >
                    Terms and Conditions
                  </button>
                </label>
              </div>
              <div className="flex flex-col space-y-3">
                <button
                  type="submit"
                  className="w-full py-2 px-4 bg-[#abbfff] hover:bg-[#4e7ab4] text-white font-semibold rounded-md transition-colors duration-300"
                >
                  Sign Up
                </button>
                <button
                  type="button"
                  onClick={() => setFormMode("login")}
                  className="text-[#4e7ab4] hover:text-[#23354d] text-sm font-medium transition-colors"
                >
                  Already have an account? Log In
                </button>
              </div>
            </form>
          </div>
        )}
      </div>
    </div>
  );
};

export default CustomerLogin;
